import {atom} from 'jotai';

export interface Notification {
    id: string;
    type: 'video' | 'silo' | '360-spin';
    status: 'processing' | 'completed' | 'failed' | 'cancelled';
    title: string;
    message: string;
    productId?: string;
    productName?: string;
    assetType?: 'product' | 'group'; // To distinguish between products and groups
    resultUrl?: string;
    thumbnailUrl?: string;
    requestId?: string;
    createdAt: string;
    completedAt?: string;
    read: boolean;
}

// Main notification list atom
export const notificationsAtom = atom<Notification[]>([]);

// Derived atom for unread count
export const unreadCountAtom = atom((get) => {
    const notifications = get(notificationsAtom);
    return notifications.filter((n) => !n.read && n.status === 'completed')
        .length;
});

// Derived atom for processing notifications
export const processingNotificationsAtom = atom((get) => {
    const notifications = get(notificationsAtom);
    return notifications.filter((n) => n.status === 'processing');
});

// Add notification atom - returns the new notification
export const addNotificationAtom = atom(
    null,
    (
        _get,
        set,
        notification: Omit<Notification, 'id' | 'createdAt' | 'read'>
    ) => {
        const id = Math.random().toString(36).substring(2, 11);
        const newNotification: Notification = {
            id,
            createdAt: new Date().toISOString(),
            read: false,
            ...notification,
        };

        // Use functional update to avoid race conditions
        set(notificationsAtom, (prev) => [newNotification, ...prev]);

        return newNotification;
    }
);

// Update notification atom
export const updateNotificationAtom = atom(
    null,
    (_get, set, update: {id: string; changes: Partial<Notification>}) => {
        set(notificationsAtom, (prev) =>
            prev.map((notification) =>
                notification.id === update.id
                    ? {...notification, ...update.changes}
                    : notification
            )
        );
    }
);

// Mark notification as read
export const markAsReadAtom = atom(null, (_get, set, id: string) => {
    set(notificationsAtom, (prev) =>
        prev.map((notification) =>
            notification.id === id
                ? {...notification, read: true}
                : notification
        )
    );
});

// Mark all as read
export const markAllAsReadAtom = atom(null, (_get, set) => {
    set(notificationsAtom, (prev) =>
        prev.map((notification) => ({
            ...notification,
            read: true,
        }))
    );
});

// Remove notification atom
export const removeNotificationAtom = atom(null, (_get, set, id: string) => {
    set(notificationsAtom, (prev) =>
        prev.filter((notification) => notification.id !== id)
    );
});

// Clear old notifications (older than X days)
export const clearOldNotificationsAtom = atom(
    null,
    (_get, set, daysOld: number = 7) => {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysOld);

        set(notificationsAtom, (prev) =>
            prev.filter((notification) => {
                const createdDate = new Date(notification.createdAt);
                return createdDate > cutoffDate;
            })
        );
    }
);
